import { formatAddressInline } from "../../../../../_shared/_logic/countries"
import { formatPropertyUnitDisplayForProperty } from "../../../../../_shared/_logic/unit-labels"
import { internalOrderTypeLabel } from "../../../../../_shared/_logic/order-type-labels"

export type WorkOrderSelectSelection = {
  id: string
  label: string
  workOrder: WorkOrder
  currentScheduleEvent?: ScheduleEvent | null
  propertyId: string | null
  propertyUnitId: string | null
  propertyUnitType?: "BUILDING" | "RESIDENTIAL" | null
  propertyLabel: string
  unitLabel: string
}

const normalizeId = (value: unknown) => `${value ?? ""}`.trim()
const nonBlockingAppointmentStatuses = new Set(["CANCELED", "NOSHOW"])

const formatCreatedDate = (value: unknown) => {
  const text = normalizeId(value)

  if (!text) return ""

  const parsed = new Date(text)

  if (Number.isNaN(parsed.getTime())) return ""

  return parsed.toLocaleDateString("de-DE")
}

const workOrderNumberLabel = (workOrder: WorkOrder) => {
  const number = normalizeId(workOrder.order_number)

  return number ? `#${number}` : "ohne Nr."
}

const compactOrderTypeLabel = (value: string) => {
  const normalized = value.trim()

  if (normalized.length <= 35) return normalized

  return `${normalized.slice(0, 35).trimEnd()}...`
}

const compactPropertyLabel = (property: Property | null) => {
  if (!property) return "Unbekannte Liegenschaft"

  const streetLine = [property.street, property.house_number]
    .map(normalizeId)
    .filter(Boolean)
    .join(" ")
  const city = normalizeId(property.city)

  return (
    [streetLine, city].filter(Boolean).join(", ") ||
    formatAddressInline(property) ||
    "Unbekannte Liegenschaft"
  )
}

export const buildWorkOrderSelectionLabel = (workOrder: WorkOrder) => {
  const typeLabel = compactOrderTypeLabel(internalOrderTypeLabel(workOrder) || "Auftrag")
  const createdDate = formatCreatedDate(workOrder.created_at)
  const suffix = createdDate ? ` (vom ${createdDate})` : ""

  return `${typeLabel} ${workOrderNumberLabel(workOrder)}${suffix}`
}

export const buildWorkOrderSelectionFromProjection = ({
  workOrder,
  property,
  scheduleEvents = [],
}: {
  workOrder: WorkOrder
  property: Property | null
  scheduleEvents?: ScheduleEvent[]
}): WorkOrderSelectSelection => {
  const propertyLabel = compactPropertyLabel(property)
  const normalizedPropertyUnitId = normalizeId(workOrder.property_unit_id)
  const propertyUnit = (property?.units ?? []).find(
    (unit) => normalizeId(unit.id) === normalizedPropertyUnitId,
  )
  const unitLabel =
    formatPropertyUnitDisplayForProperty(
      property,
      normalizedPropertyUnitId,
      normalizeId(workOrder.property_unit_label),
    ) || ""

  return {
    id: normalizeId(workOrder.id),
    label: buildWorkOrderSelectionLabel(workOrder),
    workOrder,
    currentScheduleEvent: currentScheduleEventForWorkOrder(workOrder, scheduleEvents),
    propertyId: normalizeId(workOrder.property_id) || null,
    propertyUnitId: normalizedPropertyUnitId || null,
    propertyUnitType:
      propertyUnit?.unit_type === "BUILDING"
        ? "BUILDING"
        : propertyUnit?.unit_type === "RESIDENTIAL"
          ? "RESIDENTIAL"
          : null,
    propertyLabel,
    unitLabel,
  }
}

const currentScheduleEventForWorkOrder = (
  workOrder: WorkOrder,
  scheduleEvents: ScheduleEvent[],
): ScheduleEvent | null => {
  const workOrderId = normalizeId(workOrder.id)
  const currentScheduleEventId = normalizeId(
    (workOrder as WorkOrder & { current_schedule_event_id?: string | null })
      .current_schedule_event_id,
  )

  if (currentScheduleEventId) {
    return scheduleEvents.find((event) => normalizeId(event.id) === currentScheduleEventId) ?? null
  }

  if (!workOrderId) return null

  return (
    scheduleEvents.find((event) => {
      if (normalizeId(event.work_order_id) !== workOrderId) return false

      const status = normalizeId(event.status).toUpperCase()
      const type = normalizeId(event.event_type).toUpperCase()

      return type === "APPOINTMENT" && !nonBlockingAppointmentStatuses.has(status)
    }) ?? null
  )
}

export const isWorkOrderSelectableForAppointmentCreate = ({
  workOrder,
  scheduleEvents,
  employeeId = null,
}: {
  workOrder: WorkOrder
  scheduleEvents: ScheduleEvent[]
  employeeId?: string | null
}) => {
  const workOrderId = normalizeId(workOrder.id)

  if (!workOrderId) return false

  if (!isWorkOrderAllowedForEmployee(workOrder, employeeId)) return false

  const currentScheduleEventId = normalizeId(
    (workOrder as WorkOrder & { current_schedule_event_id?: string | null })
      .current_schedule_event_id,
  )
  const relatedEvents = (scheduleEvents ?? []).filter((event) => {
    const eventWorkOrderId = normalizeId(event.work_order_id)
    const eventId = normalizeId(event.id)

    return (
      eventWorkOrderId === workOrderId ||
      Boolean(currentScheduleEventId && eventId === currentScheduleEventId)
    )
  })

  if (!relatedEvents.length) return !currentScheduleEventId

  return relatedEvents.every((event) =>
    nonBlockingAppointmentStatuses.has(normalizeId(event.status).toUpperCase()),
  )
}

export const isWorkOrderAllowedForEmployee = (workOrder: WorkOrder, employeeId?: string | null) => {
  const scopedEmployeeId = normalizeId(employeeId)

  if (!scopedEmployeeId) return true

  const allowedEmployeeIds = (workOrder.allowed_employee_ids ?? []).map(normalizeId).filter(Boolean)

  return !allowedEmployeeIds.length || allowedEmployeeIds.includes(scopedEmployeeId)
}
